import { rtkApi as api } from '../rtkApi';

export interface AgreeableClause {
  _id: string;
  contractId: string;
  clauseId: string;
  clauseHierarchicalId: string;
  agreedByUser: string;
  agreedByRole: 'buyer' | 'seller' | 'agent';
  agreedValue: any;
  isPriority: boolean;
  prioritySetBy?: string;
  priorityReason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgreeableSummary {
  total: number;
  byRole: {
    buyer: number;
    seller: number;
    agent: number;
  };
  priorityCount: number;
  clauses: {
    clauseId: string;
    agreedByRole: string;
    agreedValue: any;
    isPriority: boolean;
    agreedAt: string;
  }[];
}

export const agreeableClauseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    markClauseAsAgreeable: builder.mutation<
      { success: boolean; agreeableClause: AgreeableClause },
      {
        contractId: string;
        clauseId: string;
        clauseHierarchicalId: string;
        agreedValue: any;
        notes?: string;
      }
    >({
      query: (data) => ({
        url: '/agreeable-clauses',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AgreeableClause', 'Proposal'],
    }),

    unmarkClauseAsAgreeable: builder.mutation<
      { success: boolean },
      { contractId: string; clauseId: string }
    >({
      query: ({ contractId, clauseId }) => ({
        url: `/agreeable-clauses/${contractId}/${clauseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AgreeableClause', 'Proposal'],
    }),

    getAgreeableClauses: builder.query<AgreeableClause[], string>({
      query: (contractId) => ({
        url: `/agreeable-clauses/${contractId}`,
        method: 'GET',
      }),
      providesTags: ['AgreeableClause'],
    }),

    setClausePriority: builder.mutation<
      { success: boolean; agreeableClause: AgreeableClause },
      {
        contractId: string;
        clauseId: string;
        isPriority: boolean;
        priorityReason?: string;
      }
    >({
      query: ({ contractId, clauseId, isPriority, priorityReason }) => ({
        url: `/agreeable-clauses/${contractId}/${clauseId}/priority`,
        method: 'PUT',
        data: { isPriority, priorityReason },
      }),
      invalidatesTags: ['AgreeableClause'],
    }),

    getPriorityClauses: builder.query<AgreeableClause[], string>({
      query: (contractId) => ({
        url: `/agreeable-clauses/${contractId}/priorities`,
        method: 'GET',
      }),
      providesTags: ['AgreeableClause'],
    }),

    getAgreeableSummary: builder.query<
      AgreeableSummary,
      { contractId: string; totalClauseCount?: number }
    >({
      query: ({ contractId, totalClauseCount }) => ({
        url: `/agreeable-clauses/${contractId}/summary`,
        method: 'GET',
        params: totalClauseCount ? { totalClauseCount } : undefined,
      }),
      providesTags: ['AgreeableClause'],
    }),
  }),
});

export const {
  useMarkClauseAsAgreeableMutation,
  useUnmarkClauseAsAgreeableMutation,
  useGetAgreeableClausesQuery,
  useSetClausePriorityMutation,
  useGetPriorityClausesQuery,
  useGetAgreeableSummaryQuery,
} = agreeableClauseApi;
